/**
 * Dashboard Page
 *
 * Welcome section + quick actions only. The real-time business summary
 * (stats / low stock) and its report API calls were removed.
 */

import { useState, useCallback, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  FileText,
  Package,
  Users,
  ChevronRight,
  Calculator,
  Receipt,
  FileCheck,
  Building2,
  ShoppingCart,
  Quote as QuoteIcon,
} from 'lucide-react'

import { t } from '@/i18n'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks/useAuth'
import { useBusinessInfo } from '@/hooks/useBusinessInfo'
import { UserAvatar } from '@/components/AvatarSelector'
import { config } from '@/config'
import { getNextQuoteForAvatar, getQuoteText, type Quote as QuoteType } from '@/config/quotes'
import { getAvatarById } from '@/config/avatars'

/**
 * Quick Action Button - Touch friendly
 */
function QuickActionButton({
  icon: Icon,
  label,
  to,
}: {
  icon: React.ComponentType<{ className?: string }>
  label: string
  to: string
}) {
  return (
    <Button
      asChild
      variant="outline"
      className="h-14 md:h-12 justify-start gap-3 text-left"
    >
      <Link to={to}>
        <div className="p-2 bg-primary/10 rounded-full shrink-0">
          <Icon className="h-4 w-4 text-primary" />
        </div>
        <span className="font-medium">{label}</span>
        <ChevronRight className="h-4 w-4 text-muted-foreground ml-auto" />
      </Link>
    </Button>
  )
}

/**
 * Welcome Card Component
 */
function WelcomeCard() {
  const { user } = useAuth()
  const { data: business } = useBusinessInfo()
  const firstName = user?.full_name?.split(' ')[0] || 'Usuario'

  // Quote state - changes each time dashboard loads AND on avatar click
  const [quote, setQuote] = useState<QuoteType | null>(() => {
    if (user?.avatar) {
      // Get next quote each time dashboard loads
      return getNextQuoteForAvatar(user.avatar)
    }
    return null
  })

  // Talking animation state - starts true to animate on first load
  const [isTalking, setIsTalking] = useState(true)

  // Stop initial animation after 1.5 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsTalking(false)
    }, 1500)
    return () => clearTimeout(timer)
  }, [])

  // Update quote when avatar changes
  useEffect(() => {
    if (user?.avatar) {
      setQuote(getNextQuoteForAvatar(user.avatar))
      setIsTalking(true)
      const timer = setTimeout(() => {
        setIsTalking(false)
      }, 1500)
      return () => clearTimeout(timer)
    }
  }, [user?.avatar])

  // Handle avatar click - get next quote in sequence and trigger talking animation
  const handleAvatarClick = useCallback(() => {
    if (!user?.avatar) return

    // Start talking animation
    setIsTalking(true)

    // Get next quote in sequence (cycles through all 15)
    const nextQuote = getNextQuoteForAvatar(user.avatar)
    setQuote(nextQuote)

    // Stop talking animation after 1.5 seconds
    setTimeout(() => {
      setIsTalking(false)
    }, 1500)
  }, [user?.avatar])

  return (
    <Card className="bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border-primary/20">
      <CardContent className="p-4 md:p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          {/* Welcome Message */}
          <div className="flex items-center gap-4">
            <UserAvatar
              avatarId={user?.avatar}
              name={user?.full_name || 'Usuario'}
              size="xl"
              className="shadow-lg hidden md:flex cursor-pointer transition-all duration-300 hover:shadow-xl hover:scale-105"
              onClick={handleAvatarClick}
            />
            <UserAvatar
              avatarId={user?.avatar}
              name={user?.full_name || 'Usuario'}
              size="lg"
              className="shadow-lg md:hidden cursor-pointer active:scale-95 transition-transform"
              onClick={handleAvatarClick}
            />
            <div className="flex-1 min-w-0">
              <h2 className="text-xl md:text-2xl font-bold">
                {t('welcome.greeting')}, {firstName}!
              </h2>
              <p className="text-sm text-muted-foreground">
                {business?.name || t('welcome.businessName')}
                {' - '}
                {t('welcome.subtitle')}
              </p>
              {/* Inspirational Quote */}
              {quote && (
                <div
                  key={quote.id}
                  className="mt-2 space-y-1 animate-quote"
                >
                  <div className="flex items-start gap-2 text-sm text-muted-foreground/80 italic">
                    <QuoteIcon className={`h-4 w-4 shrink-0 mt-0.5 text-primary/50 ${isTalking ? 'animate-talking-icon' : ''}`} />
                    <span className="line-clamp-2">{getQuoteText(quote, 'es')}</span>
                  </div>
                  <p className="text-xs text-primary/70 font-medium pl-6">
                    — {getAvatarById(user?.avatar)?.name || 'Personaje'}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Hacienda Badge */}
          <div className="flex items-center gap-3 bg-card rounded-lg px-4 py-3 border">
            <div className="p-2 bg-green-500/10 rounded-full">
              <FileCheck className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm font-medium">{t('footer.haciendaVersion')}</p>
              <p className="text-xs text-muted-foreground">
                {config.hacienda.country}
              </p>
            </div>
          </div>
        </div>

        {/* Business Info (if configured) */}
        {business?.name && (
          <div className="mt-4 pt-4 border-t border-primary/10">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Building2 className="h-4 w-4" />
              <span>{business.name}</span>
              {business.identification && (
                <>
                  <span className="hidden sm:inline">|</span>
                  <span className="hidden sm:inline">{business.identification}</span>
                </>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export function DashboardPage() {
  const { isAdmin } = useAuth()

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Welcome Card */}
      <WelcomeCard />

      {/* Header - Desktop */}
      <div className="hidden md:flex items-center justify-between">
        <h1 className="text-xl md:text-2xl font-bold tracking-tight">{t('dashboard.title')}</h1>
        <Button asChild size="lg">
          <Link to="/bills/new">
            <ShoppingCart className="h-5 w-5 mr-2" />
            {t('bills.newBill')}
          </Link>
        </Button>
      </div>

      {/* Quick Actions - Mobile grid */}
      <Card className="md:hidden">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{t('dashboard.quickActions')}</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-2 pt-0">
          <Button asChild variant="outline" className="h-20 flex-col gap-2">
            <Link to="/bills/new">
              <div className="p-2.5 bg-primary/10 rounded-full">
                <FileText className="h-5 w-5 text-primary" />
              </div>
              <span className="text-xs font-medium">{t('bills.newBill')}</span>
            </Link>
          </Button>
          <Button asChild variant="outline" className="h-20 flex-col gap-2">
            <Link to="/cashbox">
              <div className="p-2.5 bg-green-500/10 rounded-full">
                <Calculator className="h-5 w-5 text-green-600" />
              </div>
              <span className="text-xs font-medium">{t('nav.cashbox')}</span>
            </Link>
          </Button>
          {isAdmin ? (
            <Button asChild variant="outline" className="h-20 flex-col gap-2">
              <Link to="/settings/invoicing-incidents">
                <div className="p-2.5 bg-red-500/10 rounded-full">
                  <FileCheck className="h-5 w-5 text-red-600" />
                </div>
                <span className="text-xs font-medium">{t('incidents.short')}</span>
              </Link>
            </Button>
          ) : (
            <Button asChild variant="outline" className="h-20 flex-col gap-2">
              <Link to="/clients">
                <div className="p-2.5 bg-blue-500/10 rounded-full">
                  <Users className="h-5 w-5 text-blue-600" />
                </div>
                <span className="text-xs font-medium">{t('nav.clients')}</span>
              </Link>
            </Button>
          )}
          <Button asChild variant="outline" className="h-20 flex-col gap-2">
            <Link to="/articles">
              <div className="p-2.5 bg-orange-500/10 rounded-full">
                <Package className="h-5 w-5 text-orange-600" />
              </div>
              <span className="text-xs font-medium">{t('nav.articles')}</span>
            </Link>
          </Button>
        </CardContent>
      </Card>

      {/* Quick Actions - Desktop */}
      <Card className="hidden md:block">
        <CardHeader>
          <CardTitle>{t('dashboard.quickActions')}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          <QuickActionButton icon={FileText} label={t('bills.newBill')} to="/bills/new" />
          <QuickActionButton icon={Calculator} label={t('nav.cashbox')} to="/cashbox" />
          <QuickActionButton icon={Package} label={t('dashboard.manageArticles')} to="/articles" />
          <QuickActionButton icon={Users} label={t('nav.clients')} to="/clients" />
          <QuickActionButton icon={Receipt} label={t('nav.reports')} to="/reports" />
          {isAdmin && (
            <QuickActionButton
              icon={FileCheck}
              label={t('incidents.titleShort')}
              to="/settings/invoicing-incidents"
            />
          )}
        </CardContent>
      </Card>
    </div>
  )
}
